import type { Network, Station, Connector } from '../data/types';
import { lineColor } from '../data/lineColors';

export interface UIHandlers {
  onToggleLine: (ref: string, on: boolean) => void;
  onAllLines: (on: boolean) => void;
  onFocusLine: (ref: string) => void;
  onSelectStation: (stationId: string) => void;
  onPreset: (name: string) => void;
  onOverheight: (v: number) => void;
  onGroundOpacity: (v: number) => void;
  onViewingDistance: (v: number) => void;
  onToggleRhein: (on: boolean) => void;
  onToggleBasemap: (on: boolean) => void;
  onToggleLevels: (on: boolean) => void;
  onToggleExplode: (on: boolean) => void;
  onTogglePause: () => void;
  onSpeed: (v: number) => void;
  onScheduledApply: (date: string, time: string) => void;
}

export class UIController {
  private root: HTMLElement;
  private lineChecks = new Map<string, HTMLInputElement>();
  private stationInfo!: HTMLElement;
  private timeReadout!: HTMLElement;
  private pauseBtn!: HTMLButtonElement;
  private handlers: UIHandlers;
  private stations: Station[];
  private selectedStations = new Set<string>();

  constructor(root: HTMLElement, network: Network, handlers: UIHandlers) {
    this.root = root;
    this.handlers = handlers;
    this.stations = network.stations;
    root.style.display = 'block';
    root.innerHTML = '';

    this.linesSection(network);
    this.cameraSection();
    this.searchSection();
    this.simulationSection();
    this.displaySection();
    this.infoSection(network);
  }

  private h(tag: string, cls?: string, text?: string): HTMLElement {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  private linesSection(network: Network) {
    const sec = this.section('Linien');
    const row = this.h('div', 'row');
    const allBtn = this.h('button', undefined, 'Alle') as HTMLButtonElement;
    const noneBtn = this.h('button', undefined, 'Keine') as HTMLButtonElement;
    allBtn.onclick = () => this.handlers.onAllLines(true);
    noneBtn.onclick = () => this.handlers.onAllLines(false);
    row.append(allBtn, noneBtn);
    sec.append(row);

    for (const line of network.lines) {
      const item = this.h('div', 'line-item');
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = true;
      cb.onchange = () => this.handlers.onToggleLine(line.ref, cb.checked);
      // line marker: coloured background + white text
      const badge = this.h('span', 'line-badge', line.ref);
      badge.style.background = lineColor(line.ref);
      badge.title = line.name;
      const focus = this.h('button', 'mini', 'Fokus') as HTMLButtonElement;
      focus.onclick = () => this.handlers.onFocusLine(line.ref);
      item.append(cb, badge, focus);
      sec.append(item);
      this.lineChecks.set(line.ref, cb);
    }
  }

  private cameraSection() {
    const sec = this.section('Kamera');
    const row = this.h('div', 'row');
    for (const name of ['Gesamtnetz', 'Düsseldorf', 'Innenstadt']) {
      const b = this.h('button', undefined, name) as HTMLButtonElement;
      b.onclick = () => this.handlers.onPreset(name);
      row.append(b);
    }
    sec.append(row);
    sec.append(this.h('div', 'dim', 'Linksziehen: verschieben · Rechtsziehen: drehen · Mausrad: Zoom zur Maus · WASD: bewegen · Q/E: Höhe'));
  }

  private searchSection() {
    const sec = this.section('Stationssuche');
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Haltestelle…';
    input.className = 'search';
    const list = this.h('div', 'results');
    input.oninput = () => {
      const q = input.value.toLowerCase().trim();
      list.innerHTML = '';
      if (q.length < 2) return;
      const matches = this.stations.filter((s) => s.name.toLowerCase().includes(q)).slice(0, 12);
      for (const s of matches) {
        const b = this.h('button', 'result', `${s.name} — ${s.lines.join(', ')}`) as HTMLButtonElement;
        b.onclick = () => {
          this.handlers.onSelectStation(s.id);
          list.innerHTML = '';
          input.value = s.name;
          this.showStation(s);
        };
        list.append(b);
      }
    };
    sec.append(input, list);
    this.stationInfo = this.h('div', 'station-info', 'Keine Station gewählt.');
    sec.append(this.stationInfo);
  }

  showStation(s: Station) {
    this.selectedStations.clear();
    this.selectedStations.add(s.id);
    this.stationInfo.innerHTML = this.stationHtml(s);
  }

  stationHtml(s: Station): string {
    const lines = s.lines.join(', ');
    return (
      `<strong>${s.name}</strong><br>` +
      `Linien: ${lines || '—'}<br>` +
      `Lage: ${s.lat.toFixed(5)}, ${s.lon.toFixed(5)}<br>` +
      `<span class="dim">Quelle: VRR GTFS + OSM (S03/S11).</span>`
    );
  }

  connectorHtml(c: Connector): string {
    const q = c.quality === 'derived' ? 'belegt' : 'angenähert';
    return (
      `<strong>${c.stationName}</strong><br>` +
      `Verbindung: ${c.from.label} → ${c.to.label}<br>` +
      `Art: ${c.kind} · ${q}<br>` +
      `Quelle: ${c.source}<br>` +
      `<span class="dim">${c.note}</span>`
    );
  }

  private simulationSection() {
    const sec = this.section('Fahrplan');

    const ctrlRow = this.h('div', 'row');
    this.pauseBtn = this.h('button', undefined, 'Pause') as HTMLButtonElement;
    this.pauseBtn.onclick = () => {
      this.handlers.onTogglePause();
      this.pauseBtn.textContent = this.pauseBtn.textContent === 'Pause' ? 'Weiter' : 'Pause';
    };
    const s1 = this.h('button', undefined, '1×') as HTMLButtonElement;
    const s10 = this.h('button', undefined, '10×') as HTMLButtonElement;
    const s60 = this.h('button', undefined, '60×') as HTMLButtonElement;
    s1.onclick = () => this.handlers.onSpeed(1);
    s10.onclick = () => this.handlers.onSpeed(10);
    s60.onclick = () => this.handlers.onSpeed(60);
    ctrlRow.append(this.pauseBtn, s1, s10, s60);
    sec.append(ctrlRow);

    this.timeReadout = this.h('div', 'readout', '08:00:00');
    sec.append(this.timeReadout);

    this.scheduleRow = this.h('div', 'row schedule-row');
    const date = document.createElement('input');
    date.type = 'date';
    date.value = berlinNow().date;
    const time = document.createElement('input');
    time.type = 'time';
    time.value = berlinNow().time;
    const nowBtn = this.h('button', 'mini', 'Aktuelle Zeit') as HTMLButtonElement;
    nowBtn.onclick = () => {
      const { date: d, time: t } = berlinNow();
      date.value = d;
      time.value = t;
      this.handlers.onScheduledApply(d, t);
    };
    const apply = this.h('button', undefined, 'Anwenden') as HTMLButtonElement;
    apply.onclick = () => this.handlers.onScheduledApply(date.value, time.value);
    this.scheduleRow.append(date, time, nowBtn, apply);
    sec.append(this.scheduleRow);
    this.dateInput = date;
    this.timeInput = time;
  }
  private scheduleRow!: HTMLElement;
  private dateInput!: HTMLInputElement;
  private timeInput!: HTMLInputElement;

  private displaySection() {
    const sec = this.section('Darstellung');
    sec.append(this.check('Karte (OSM) anzeigen', false, (v) => this.handlers.onToggleBasemap(v)));
    sec.append(this.check('Rhein anzeigen', true, (v) => this.handlers.onToggleRhein(v)));
    sec.append(this.check('Ebenen & Verbindungsgänge', false, (v) => this.handlers.onToggleLevels(v)));
    sec.append(this.check('Explosionsdarstellung', false, (v) => this.handlers.onToggleExplode(v)));
    sec.append(this.slider('Boden-Transparenz', 0, 100, 12, (v) => this.handlers.onGroundOpacity(v / 100)));
    sec.append(this.slider('Zug-Sichtweite', 1, 10, 10, (v) => this.handlers.onViewingDistance(v)));
    const over = this.slider('Vertikale Überhöhung', 1, 6, 1, (v) => this.handlers.onOverheight(v));
    over.querySelector('input')!.step = '0.5';
    sec.append(over);
    const reset = this.h('button', undefined, 'Ansicht zurücksetzen') as HTMLButtonElement;
    reset.onclick = () => this.handlers.onPreset('Gesamtnetz');
    sec.append(reset);
  }

  private check(label: string, val: boolean, cb: (v: boolean) => void): HTMLElement {
    const wrap = this.h('label', 'checkrow');
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = val;
    input.onchange = () => cb(input.checked);
    const span = this.h('span', undefined, label);
    wrap.append(input, span);
    return wrap;
  }

  private slider(label: string, min: number, max: number, val: number, cb: (v: number) => void): HTMLElement {
    const wrap = this.h('div', 'slider');
    const l = this.h('label', undefined, label) as HTMLLabelElement;
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.value = String(val);
    const out = this.h('span', 'slider-val', String(val));
    input.oninput = () => {
      out.textContent = input.value;
      cb(Number(input.value));
    };
    wrap.append(l, input, out);
    return wrap;
  }

  private infoSection(network: Network) {
    const sec = this.section('Daten & Status');
    const c = network.meta.counts;
    const issueCounts: Record<string, number> = {};
    for (const i of network.issues) issueCounts[i.id] = (issueCounts[i.id] || 0) + 1;
    sec.innerHTML +=
      `<div class="dim">Snapshot ${network.meta.generatedAt.slice(0, 10)}<br>` +
      `Kanten: ${c.edgeCount} · Linien: ${network.lines.length} · Stationen: ${network.stations.length}<br>` +
      `Bauwerke: ${c.structures} · Muster gematcht: ${c.matchedPatterns}/${c.matchedPatterns + c.unmatchedPatterns}<br>` +
      `Mehrstöckige Stationen: ${c.multiLevelStations ?? 0} · Verbindungsgänge: ${c.connectors ?? 0}<br>` +
      `Offene Datenhinweise: ${network.issues.length}</div>`;
    const details = this.h('details');
    const summary = this.h('summary', undefined, 'Datenhinweise anzeigen');
    details.append(summary);
    const list = this.h('ul', 'issues');
    for (const [k, v] of Object.entries(issueCounts)) {
      list.append(this.h('li', undefined, `${k}: ${v}`));
    }
    details.append(list);
    sec.append(details);
    sec.append(this.h('div', 'dim', 'Quellen: VRR GTFS (CC-BY) · OpenStreetMap (ODbL), inkl. Rhein (Fluss, schematische Breite). Keine Live-Positionen, keine vermessungsgenaue Tiefe.'));
  }

  private section(title: string): HTMLElement {
    const sec = this.h('section', 'panel-section');
    sec.append(this.h('h3', undefined, title));
    this.root.append(sec);
    return sec;
  }

  setTime(text: string) {
    this.timeReadout.textContent = text;
  }

  setLinesSelected(refs: string[]) {
    for (const [ref, cb] of this.lineChecks) cb.checked = refs.includes(ref);
  }

  setScheduledDate(date: string, time: string) {
    this.dateInput.value = date;
    this.timeInput.value = time;
  }
}

/** Current local date/time in Europe/Berlin as { date: 'YYYY-MM-DD', time: 'HH:MM' }. */
export function berlinNow(now: Date = new Date()): { date: string; time: string } {
  const date = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(now);
  const time = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(now);
  return { date, time };
}
